// An inline object (section 6.4): the inline template's parameters as nested fields; a field edit is one change at
// `data.<param>.<field>`; Clear writes null, Set writes the defaults.
import type { Json } from '../../api/types';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { Plus, X } from '../../ui/icons';
import { useContent } from '../ContentContext';
import { parameterHelp } from '../help';
import { typeDefault } from '../schema';
import { FieldWidget } from './FieldWidget';
import { joinPath, type WidgetProps } from './context';

export function InlineObjectField({ param: p, value, onCommit, onCommitAt, disabled, id, owner }: WidgetProps) {
  const catalog = useContent();
  const inline = p.template ? catalog.templates.get(p.template) : undefined;
  const object = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, Json>) : null;
  if (!inline) {
    return <code className="small muted">{object ? JSON.stringify(object).slice(0, 120) : 'null'}</code>;
  }
  const defaults = () => {
    const out: Record<string, Json> = {};
    for (const f of inline.effectiveParameters) out[f.name] = typeDefault(f);
    return out;
  };
  if (!object) {
    return (
      <span className="row" id={id}>
        <span className="muted">None</span>
        {!disabled && (
          <Button size="s" onClick={() => onCommit(defaults())} icon={<Plus aria-hidden="true" />}>
            Set {inline.displayName ?? inline.name.split('.').pop()}
          </Button>
        )}
      </span>
    );
  }
  const commitField = (name: string, v: Json) => {
    if (onCommitAt) onCommitAt(name, v);
    else onCommit({ ...object, [name]: v });
  };
  return (
    <div className="inline-group" id={id} data-testid={`object-${p.name}`}>
      <div className="row inline-group-head">
        <Badge>{inline.displayName ?? inline.name.split('.').pop()}</Badge>
        <span className="spacer" />
        {!disabled && (
          <Button size="s" variant="ghost" onClick={() => onCommit(null)} icon={<X aria-hidden="true" />}>
            Clear
          </Button>
        )}
      </div>
      {inline.effectiveParameters.map((f) => (
        <Field key={f.name} label={f.displayName ?? f.name} help={parameterHelp(f, { template: inline.name }, catalog.templates)} inline>
          <FieldWidget param={f} value={object[f.name]} disabled={disabled} owner={owner} onCommit={(v) => commitField(f.name, v)} onCommitAt={(rel, v) => (onCommitAt ? onCommitAt(joinPath(f.name, rel), v) : commitField(f.name, setRel(object[f.name], rel, v)))} />
        </Field>
      ))}
    </div>
  );
}

/** A relative path set inside a value (used when the owner commits whole values). */
export function setRel(value: Json | undefined, rel: string, v: Json): Json {
  const m = /^(?:\[(\d+)\]|([^.[\]]+))(.*)$/.exec(rel);
  if (!m) return v;
  const [, index, key, rest] = m;
  if (index !== undefined) {
    const arr = Array.isArray(value) ? [...value] : [];
    const i = Number(index);
    arr[i] = rest ? setRel(arr[i], rest.replace(/^\./, ''), v) : v;
    return arr;
  }
  const obj = value && typeof value === 'object' && !Array.isArray(value) ? { ...(value as Record<string, Json>) } : {};
  obj[key!] = rest ? setRel(obj[key!], rest.replace(/^\./, ''), v) : v;
  return obj;
}
